import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../services/api';
import {
  FiClock, FiUser, FiSettings, FiLogOut,
  FiActivity, FiMonitor, FiX, FiEdit2, FiCheck,
  FiLink
} from 'react-icons/fi';
import { useToast } from '../hooks/useToast';
import LinkedDevicesManager from './LinkedDevicesManager';
import favicon from '../assets/images/favicon.svg';
import './SessionInfo.css';

function SessionInfo({ onLogout }) {
  const { t } = useTranslation();
  const { success, error, warning } = useToast();
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [editingAlias, setEditingAlias] = useState(false);
  const [newAlias, setNewAlias] = useState('');
  const [sessionConfig, setSessionConfig] = useState({
    timeout: '24h',
    autoRenew: true
  });
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [showDevices, setShowDevices] = useState(false);

  const timeoutOptions = [
    { value: 'never', label: t('session.timeouts.never') },
    { value: 'browser', label: t('session.timeouts.browser') },
    { value: '5m', label: t('session.timeouts.5m') },
    { value: '15m', label: t('session.timeouts.15m') },
    { value: '30m', label: t('session.timeouts.30m') },
    { value: '1h', label: t('session.timeouts.1h') },
    { value: '2h', label: t('session.timeouts.2h') },
    { value: '4h', label: t('session.timeouts.4h') },
    { value: '8h', label: t('session.timeouts.8h') },
    { value: '24h', label: t('session.timeouts.24h') }
  ];

  useEffect(() => {
    fetchSessionInfo();
    const interval = setInterval(fetchSessionInfo, 300000); // Actualizar cada 5 min (antes 2 min)
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSessionInfo = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/api/cards/info/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSessionInfo(response.data);
        setNewAlias(response.data.card.alias);
        setSessionConfig(response.data.card.sessionConfig);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error al obtener información de sesión:', err);
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/api/cards/audit/logs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAuditLogs(response.data.logs);
      }
    } catch (err) {
      console.error('Error al obtener logs de auditoría:', err);
      error(t('audit.loadError'));
    }
  };

  const handleUpdateAlias = async () => {
    if (!newAlias.trim()) {
      warning(t('session.aliasEmpty'));
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.put(`${API_BASE}/api/cards/alias/update`, 
        { alias: newAlias.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        success(t('session.aliasUpdated'));
        setEditingAlias(false);
        await fetchSessionInfo();
      }
    } catch (err) {
      console.error('Error al actualizar alias:', err);
      error(t('errors.generic'));
    }
  };

  const handleCancelEditAlias = () => {
    setNewAlias(sessionInfo.card.alias); // Restaurar valor original
    setEditingAlias(false);
  };

  const handleUpdateSessionConfig = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.put(`${API_BASE}/api/cards/session-config/update`,
        sessionConfig,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { sessionAction, affectedSessions } = response.data;
        const timeoutLabel = timeoutOptions.find(opt => opt.value === sessionConfig.timeout)?.label || sessionConfig.timeout;

        // Update localStorage so beforeunload handler knows the current session type
        // (el efectivo: si la política de la organización es más estricta, manda esa)
        const effective = response.data.effectiveSessionConfig;
        localStorage.setItem('sessionTimeout', effective?.timeout || sessionConfig.timeout);
        if (effective) localStorage.setItem('sessionLimitedByOrg', effective.limitedByOrg ? '1' : '0');

        // Build detailed notification message
        let message = t('session.configUpdated');
        if (sessionAction === 'extended') {
          message += ' — ' + t('session.sessionsExtended', { count: affectedSessions, timeout: timeoutLabel });
        } else if (sessionAction === 'reduced') {
          message += ' — ' + t('session.sessionsReduced', { count: affectedSessions, timeout: timeoutLabel });
        }

        success(message);

        // Close the settings modal
        setShowSettings(false);

        // Refresh session info in background
        await fetchSessionInfo();
      }
    } catch (err) {
      console.error('Error al actualizar configuración:', err);
      error(t('errors.generic'));
    }
  };

  const handleCloseSession = async () => {
    if (!window.confirm(t('session.closeSessionConfirm'))) return;

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_BASE}/api/cards/session/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      success(t('session.sessionClosed'));
      localStorage.removeItem('authToken');
      localStorage.removeItem('rfidId');
      if (onLogout) onLogout();
    } catch (err) {
      console.error('Error al cerrar sesión:', err);
      error(t('errors.generic'));
    }
  };

  const handleCloseAllSessions = async () => {
    if (!window.confirm(t('session.closeAllConfirm'))) return;

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_BASE}/api/cards/sessions/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      success(t('session.allSessionsClosed'));
      localStorage.removeItem('authToken');
      localStorage.removeItem('rfidId');
      if (onLogout) onLogout();
    } catch (err) {
      console.error('Error al cerrar sesiones:', err);
      error(t('session.errorCloseAllSessions'));
    }
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionLabel = (action) => {
    return t(`audit.actions.${action}`, action);
  };

  const getDataTypeLabel = (dataType) => {
    return t(`vault.categories.${dataType}`, dataType);
  };

  const translateDetailKey = (key) => {
    return t(`audit.detailKeys.${key}`, key);
  };

  const translateDetailValue = (value) => {
    const strValue = String(value);
    if (strValue === 'true' || strValue === 'false') {
      return t(`audit.detailKeys.${strValue}`, strValue);
    }
    if (strValue === 'manual' || strValue === 'automatic' || strValue === 'rfid') {
      return t(`audit.detailKeys.${strValue}`, strValue);
    }
    return strValue;
  };

  const renderAuditDetails = (log) => {
    if (!log.details || Object.keys(log.details).length === 0) return null;

    switch (log.action) {
      case 'login_attempt':
        return (
          <div className="audit-detail-item">
            {log.details.success ? (
              <>
                <span className="detail-label">{t('audit.labels.status')}:</span>
                <span className="detail-value success">{t('audit.labels.successful')}</span>
              </>
            ) : (
              <>
                <span className="detail-label">{t('audit.labels.status')}:</span>
                <span className="detail-value error">{t('audit.labels.failed')}</span>
              </>
            )}
            {log.details.cardName && (
              <>
                <span className="detail-label">{t('audit.labels.card')}:</span>
                <span className="detail-value">{log.details.cardName}</span>
              </>
            )}
          </div>
        );
      
      case 'data_save':
        return (
          <div className="audit-detail-item">
            {log.details.name && (
              <>
                <span className="detail-label">{t('audit.labels.item')}:</span>
                <span className="detail-value">{log.details.name}</span>
              </>
            )}
            {log.details.data?.type && (
              <>
                <span className="detail-label">{t('audit.labels.category')}:</span>
                <span className="detail-value">{getDataTypeLabel(log.details.data.type)}</span>
              </>
            )}
          </div>
        );
      
      case 'vault_created':
        return (
          <div className="audit-detail-item">
            {log.details.vaultName && (
              <>
                <span className="detail-label">{t('audit.labels.vault')}:</span>
                <span className="detail-value">{log.details.vaultName}</span>
              </>
            )}
          </div>
        );
      
      case 'alias_updated':
        return (
          <div className="audit-detail-item">
            {log.details.oldAlias && (
              <>
                <span className="detail-label">{t('audit.labels.previous')}:</span>
                <span className="detail-value">{log.details.oldAlias}</span>
              </>
            )}
            {log.details.newAlias && (
              <>
                <span className="detail-label">{t('audit.labels.new')}:</span>
                <span className="detail-value">{log.details.newAlias}</span>
              </>
            )}
          </div>
        );
      
      case 'session_config_updated':
        return (
          <div className="audit-detail-item">
            {log.details.oldTimeout && log.details.newTimeout && log.details.oldTimeout !== log.details.newTimeout && (
              <>
                <span className="detail-label">{t('audit.labels.oldTimeout')}:</span>
                <span className="detail-value">
                  {timeoutOptions.find(opt => opt.value === log.details.oldTimeout)?.label || log.details.oldTimeout}
                </span>
                <span className="detail-label">{t('audit.labels.newTimeout')}:</span>
                <span className="detail-value">
                  {timeoutOptions.find(opt => opt.value === log.details.newTimeout)?.label || log.details.newTimeout}
                </span>
              </>
            )}
            {log.details.timeout && (!log.details.oldTimeout || log.details.oldTimeout === log.details.newTimeout) && (
              <>
                <span className="detail-label">{t('audit.labels.expirationTime')}:</span>
                <span className="detail-value">
                  {timeoutOptions.find(opt => opt.value === log.details.timeout)?.label || log.details.timeout}
                </span>
              </>
            )}
            {log.details.autoRenew !== undefined && (
              <>
                <span className="detail-label">{t('audit.labels.autoRenewal')}:</span>
                <span className="detail-value">{log.details.autoRenew ? t('audit.labels.enabled') : t('audit.labels.disabled')}</span>
              </>
            )}
            {log.details.sessionAction && log.details.sessionAction !== 'unchanged' && (
              <>
                <span className="detail-label">{t('audit.labels.sessionAction')}:</span>
                <span className={`detail-value ${log.details.sessionAction === 'extended' ? 'success' : 'warning'}`}>
                  {t(`audit.labels.${log.details.sessionAction}`)}
                </span>
              </>
            )}
            {log.details.affectedSessions > 0 && (
              <>
                <span className="detail-label">{t('audit.labels.affectedSessions')}:</span>
                <span className="detail-value">{log.details.affectedSessions}</span>
              </>
            )}
          </div>
        );
      
      case 'card_auto_registered':
        return (
          <div className="audit-detail-item">
            {log.details.autoRegistered && (
              <>
                <span className="detail-label">{t('audit.labels.autoRegister')}:</span>
                <span className="detail-value success">{t('audit.labels.yes')}</span>
              </>
            )}
          </div>
        );
      
      default:
        return (
          <div className="audit-detail-raw">
            {Object.entries(log.details).map(([key, value]) => (
              <div key={key} className="detail-row">
                <span className="detail-label">{translateDetailKey(key)}:</span>
                <span className="detail-value">{translateDetailValue(value)}</span>
              </div>
            ))}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="session-info-container">
        <div className="session-loading">{t('session.loadingInfo')}</div>
      </div>
    );
  }

  if (!sessionInfo) {
    return null;
  }

  return (
    <>
      <div className="session-info-container">
        <button className="session-info-trigger" onClick={() => setShowSettings(!showSettings)}>
          <FiUser size={20} />
          <div className="session-info-summary">
            <span className="session-alias">{sessionInfo.card.alias}</span>
            <span className="session-time">
              {sessionInfo.currentSession && formatDuration(sessionInfo.currentSession.duration)}
            </span>
          </div>
          <FiSettings size={16} />
        </button>

        {showSettings && (
          <>
            <div className="session-dropdown-overlay" onClick={() => setShowSettings(false)}></div>
            <div className="session-dropdown">
              <div className="session-dropdown-header">
                <h3>{t('session.info')}</h3>
                <button onClick={() => setShowSettings(false)}>
                  <FiX />
                </button>
              </div>

              <div className="session-dropdown-content">
              {/* Alias de tarjeta */}
              <div className="session-section">
                <label><img src={favicon} alt="" style={{ width: '16px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }} /> {t('session.alias')}</label>
                {editingAlias ? (
                  <div className="alias-edit-group">
                    <input
                      type="text"
                      value={newAlias}
                      onChange={(e) => setNewAlias(e.target.value)}
                      placeholder={t('session.aliasPlaceholder')}
                      autoFocus
                    />
                    <button className="btn-icon-success" onClick={handleUpdateAlias}>
                      <FiCheck />
                    </button>
                    <button className="btn-icon" onClick={handleCancelEditAlias}>
                      <FiX />
                    </button>
                  </div>
                ) : (
                  <div className="alias-display">
                    <span>{sessionInfo.card.alias}</span>
                    <button className="btn-icon" onClick={() => {
                      setNewAlias(sessionInfo.card.alias);
                      setEditingAlias(true);
                    }}>
                      <FiEdit2 size={14} />
                    </button>
                  </div>
                )}
                <small>RFID: {sessionInfo.card.rfidId}</small>
              </div>

              {/* Información de sesión actual */}
              {sessionInfo.currentSession && (
                <div className="session-section">
                  <label><FiClock /> {t('session.currentSession')}</label>
                  <div className="session-stats">
                    <div className="session-stat">
                      <span className="stat-label">{t('session.duration')}</span>
                      <span className="stat-value">
                        {formatDuration(sessionInfo.currentSession.duration)}
                      </span>
                    </div>
                    <div className="session-stat">
                      <span className="stat-label">{t('session.startedAt')}</span>
                      <span className="stat-value">
                        {formatDate(sessionInfo.currentSession.createdAt)}
                      </span>
                    </div>
                    <div className="session-stat">
                      <span className="stat-label">{t('session.expires')}</span>
                      <span className="stat-value">
                        {formatDate(sessionInfo.currentSession.expiresAt)}
                      </span>
                    </div>
                    <div className="session-stat">
                      <span className="stat-label">{t('session.ipAddress')}</span>
                      <span className="stat-value">
                        {sessionInfo.currentSession.ipAddress}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Sesiones activas */}
              {sessionInfo.activeSessions > 1 && (
                <div className="session-section session-warning">
                  <FiMonitor />
                  <span>{sessionInfo.activeSessions} {t('session.activeSessions').toLowerCase()}</span>
                </div>
              )}

              {/* Configuración de sesión */}
              <div className="session-section">
                <label><FiSettings /> {t('session.sessionConfig')}</label>
                <div className="session-config-group">
                  <div className="form-group">
                    <label>{t('session.timeout')}</label>
                    <select
                      value={sessionConfig.timeout}
                      onChange={(e) => setSessionConfig({...sessionConfig, timeout: e.target.value})}
                    >
                      {timeoutOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group-checkbox">
                    <input
                      type="checkbox"
                      id="autoRenew"
                      checked={sessionConfig.autoRenew}
                      onChange={(e) => setSessionConfig({...sessionConfig, autoRenew: e.target.checked})}
                    />
                    <label htmlFor="autoRenew">{t('session.autoRenew')}</label>
                  </div>

                  {sessionInfo?.effectiveSessionConfig?.limitedByOrg && (
                    <p className="session-org-limit">
                      {t('session.limitedByOrg', { timeout: t(`session.timeouts.${sessionInfo.effectiveSessionConfig.timeout}`) })}
                    </p>
                  )}

                  <button className="btn-primary-small" onClick={handleUpdateSessionConfig}>
                    {t('session.saveConfig')}
                  </button>
                </div>
              </div>

              {/* Linked Devices & Auth Methods */}
              <div className="session-section">
                <button 
                  className="btn-secondary-small" 
                  onClick={() => {
                    setShowDevices(true);
                    setShowSettings(false);
                  }}
                >
                  <FiLink /> {t('devices.manageDevices')}
                </button>
              </div>

              {/* Auditoría */}
              <div className="session-section">
                <button 
                  className="btn-secondary-small" 
                  onClick={() => {
                    setShowAudit(true);
                    fetchAuditLogs();
                  }}
                >
                  <FiActivity /> {t('audit.viewLogs')}
                </button>
              </div>

              {/* Acciones */}
              <div className="session-actions">
                <button className="btn-secondary" onClick={handleCloseSession}>
                  <FiLogOut /> {t('session.closeSession')}
                </button>
                {sessionInfo.activeSessions > 1 && (
                  <button className="btn-danger" onClick={handleCloseAllSessions}>
                    {t('session.closeAllSessions')}
                  </button>
                )}
              </div>
            </div>
          </div>
          </>
        )}
      </div>

      {/* Modal de Auditoría */}
      {showAudit && (
        <div className="modal-overlay" onClick={() => setShowAudit(false)}>
          <div className="modal-content modal-audit" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FiActivity /> {t('audit.title')}</h2>
              <button onClick={() => setShowAudit(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {auditLogs.length === 0 ? (
                <div className="empty-state">
                  <p>{t('audit.noLogs')}</p>
                </div>
              ) : (
                <div className="audit-logs">
                  {auditLogs.map((log, index) => (
                    <div key={index} className="audit-log-entry">
                      <div className="audit-log-header">
                        <span className="audit-action">{getActionLabel(log.action)}</span>
                        <span className="audit-time">{formatDate(log.timestamp)}</span>
                      </div>
                      {renderAuditDetails(log)}
                      <div className="audit-log-meta">
                        <span>IP: {log.ipAddress}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Modal de Linked Devices */}
      {showDevices && (
        <LinkedDevicesManager onClose={() => setShowDevices(false)} />
      )}
    </>
  );
}

export default SessionInfo;
